/**
 * Composite Gauge Component
 */

import { el } from '../../shared/components/simpleRender.js';

const STATUS_CONFIG = {
  calm: { label: 'CALM', color: 'var(--tg-fog-green)', bg: 'rgba(151, 184, 160, 0.15)' },
  elevated: { label: 'ELEVATED', color: '#f97316', bg: 'rgba(249, 115, 22, 0.15)' },
  warning: { label: 'WARNING', color: 'var(--tg-lantern-gold)', bg: 'rgba(212, 168, 95, 0.15)' },
  danger: { label: 'DANGER', color: 'var(--tg-rust-red)', bg: 'rgba(178, 51, 42, 0.15)' },
};

export function getGaugeStatus(value, type = 'stress') {
  if (type === 'recession') {
    if (value >= 60) return 'danger';
    if (value >= 45) return 'warning';
    if (value >= 30) return 'elevated';
    return 'calm';
  }
  // stress index
  if (value >= 70) return 'danger';
  if (value >= 55) return 'warning';
  if (value >= 40) return 'elevated';
  return 'calm';
}

function getBarColor(status) {
  switch (status) {
    case 'danger': return 'linear-gradient(90deg, var(--tg-rust-red), var(--tg-flash-crash))';
    case 'warning': return 'linear-gradient(90deg, #f97316, var(--tg-lantern-gold))';
    case 'elevated': return 'linear-gradient(90deg, var(--tg-fog-green), #f97316)';
    default: return 'linear-gradient(90deg, var(--tg-fog-green), var(--tg-liquidity-cyan))';
  }
}

export function createCompositeGauge(config) {
  const { id, title, value = 0, status = 'calm', description, components = [], updatedAt } = config;
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.calm;

  return el('div', { className: 'composite-gauge', 'data-gauge-id': id }, [
    el('div', { className: 'composite-gauge__header' }, [
      el('h3', { className: 'composite-gauge__title' }, [title]),
      el('span', {
        className: 'composite-gauge__status',
        style: `color: ${statusCfg.color}; background: ${statusCfg.bg}; border-color: ${statusCfg.color}`
      }, [statusCfg.label]),
    ]),
    el('div', { className: 'composite-gauge__visual' }, [
      el('div', { className: 'composite-gauge__bar-bg' }, [
        el('div', {
          className: 'composite-gauge__bar-fill',
          style: `width: ${Math.min(100, Math.max(0, value))}%; background: ${getBarColor(status)}`
        }, []),
        el('div', { className: 'composite-gauge__markers' }, [
          el('span', { className: 'composite-gauge__marker', style: 'left: 35%' }, []),
          el('span', { className: 'composite-gauge__marker', style: 'left: 50%' }, []),
          el('span', { className: 'composite-gauge__marker composite-gauge__marker--danger', style: 'left: 65%' }, []),
          el('span', { className: 'composite-gauge__marker composite-gauge__marker--danger', style: 'left: 80%' }, []),
        ]),
      ]),
    ]),
    el('div', { className: 'composite-gauge__value-row' }, [
      el('span', { className: 'composite-gauge__value', style: `color: ${statusCfg.color}` }, [String(Math.round(value))]),
      el('span', { className: 'composite-gauge__max' }, ['/ 100']),
    ]),
    description ? el('p', { className: 'composite-gauge__description' }, [description]) : null,
    components.length > 0 ? el('div', { className: 'composite-gauge__components' }, [
      el('span', { className: 'composite-gauge__components-label' }, ['Components: ']),
      components.join(', '),
    ]) : null,
    updatedAt ? el('div', { className: 'composite-gauge__updated' }, [`Updated: ${updatedAt}`]) : null,
  ].filter(Boolean));
}

export function updateCompositeGauge(gaugeEl, value, status) {
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.calm;
  
  const fill = gaugeEl.querySelector('.composite-gauge__bar-fill');
  if (fill) {
    fill.style.width = `${Math.min(100, Math.max(0, value))}%`;
    fill.style.background = getBarColor(status);
  }
  
  const valueEl = gaugeEl.querySelector('.composite-gauge__value');
  if (valueEl) {
    valueEl.textContent = String(Math.round(value));
    valueEl.style.color = statusCfg.color;
  }
  
  const statusEl = gaugeEl.querySelector('.composite-gauge__status');
  if (statusEl) {
    statusEl.textContent = statusCfg.label;
    statusEl.style.color = statusCfg.color;
    statusEl.style.background = statusCfg.bg;
    statusEl.style.borderColor = statusCfg.color;
  }
}
